import type { CrudFormProps, DeliveryZone, GeoPoint } from '@@partner:types:app'
import type { GQL_ID } from '@@partner:types:graphql'
import type { CourierFormState } from './CourierFormState'

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { mergeLeft, mergeLeftOnly } from 'merge-left-utils'
import { useFetchSingleCourier } from '@@partner:graphql/query/employees/singleCourier.gql'
import { useMutateSaveCourier } from '@@partner:graphql/mutation/employees/saveCourier.gql'
import { useValidatableForm } from '@@partner:helpers/hooks/rsuite'
import {
  notifyOk,
  notifyError,
  makeGraphQLErrorNotification
} from '@@partner:helpers/notification'
import {
  makeCourierFormState,
  mapApiDataToState,
  mapStateToApiVariable
} from './CourierFormState'

import { CheckPicker, Form, Panel, SelectPicker, Tag, TagGroup, Loader } from 'rsuite'
import CustomerPhone from '@components/order/form/CustomerPhone'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'
import { SaveButton } from '@components/common/form/Buttons'

import { CourierFormValidationModel } from '../validation/CourierFormValidationModel'

import { EMPLOYEE_STATUS_LABELS } from '@@partner:config/employee'
import FormCheckbox from '@components/common/form/FormCheckbox'
import { useFetchShops } from '@@partner:graphql/query/shop/shops.gql'
import { useFetchZonesInGeoPolygon } from '@@partner:graphql/query/deliveryZone/zonesInGeoPolygon.gql'

import L, { LatLngLiteral, Map as LeafletMap } from 'leaflet'
import { MapContainer, TileLayer } from 'react-leaflet'
import 'leaflet/dist/leaflet.css'

import './CourierForm.scss'

const DEFAULT_MAP_CENTER: LatLngLiteral = { lat: 50.326044, lng: 40.480974 }
const DEFAULT_MAP_ZOOM = 3

// Палитра цветов для магазинов
const SHOP_COLORS = [
  '#e74c3c', '#3498db', '#2ecc71', '#9b59b6', '#f39c12',
  '#1abc9c', '#e91e63', '#00bcd4', '#ff5722', '#795548'
]

const getShopColor = (index: number): string =>
  SHOP_COLORS[index % SHOP_COLORS.length]

const convertGeoPointToLatLng = (point: GeoPoint): LatLngLiteral => ({
  lat: point.latitude as number,
  lng: point.longitude as number
})

export interface CourierFormProps extends CrudFormProps {
  id?: GQL_ID
}

export default function CourierForm ({ id, onSaved }: CourierFormProps) {
  const { data, loading: loadingCourier, error } = useFetchSingleCourier({ id })

  const { data: shopsData, loading: loadingShopsData } = useFetchShops()
  const { data: zonesData, loading: loadingZonesData } = useFetchZonesInGeoPolygon({
    corners: [
      { latitude: 90, longitude: -180 },
      { latitude: 90, longitude: 180 },
      { latitude: -90, longitude: 180 },
      { latitude: -90, longitude: -180 }
    ]
  })

  const [ map, setMap ] = useState<LeafletMap>()
  const [ polygonsOnMap, setPolygonsOnMap ] = useState<L.Polygon[]>([])

  const shops = useMemo(
    () =>
      shopsData?.shops?.map(
        (item) => ({ label: `${item.name} ${item.address}`, value: Number(item.id) })
      ) ?? [],
    [ shopsData?.shops ]
  )

  const allZones: DeliveryZone[] = useMemo(
    () => zonesData?.zonesInGeoPolygon ?? [],
    [ zonesData?.zonesInGeoPolygon ]
  )

  const zonesPickerData = useMemo(
    () => allZones.map((zone) => ({
      label: `${zone.name || `#${zone.id}`}${zone.shop?.name ? ` — ${zone.shop.name}` : ''}`,
      value: Number(zone.id)
    })),
    [ allZones ]
  )

  const [ saveCourier, { loading: loadingSaveCourier } ] = useMutateSaveCourier()

  const {
    formState,
    setFormState,
    setFormRef,
    handleFormChange,
    handleFormError
  } = useValidatableForm<CourierFormState>(makeCourierFormState)

  const selectedShops = shops.filter(shop => formState.shopIds?.includes(shop.value))
  const selectedDeliveryZones = allZones.filter(zone =>
    formState.deliveryZoneIds?.includes(Number(zone.id))
  )

  useEffect(() => {
    if (!loadingCourier && data) {
      setFormState((state) =>
        mergeLeft(state, mapApiDataToState(data?.courier))
      )
    }
  }, [ data, loadingCourier, setFormState ])

  // Переключение выбора зоны
  const toggleZoneSelection = useCallback(
    (zoneId: GQL_ID) => {
      const numericId = Number(zoneId)
      setFormState((state) => {
        const currentIds = state.deliveryZoneIds ?? []
        const isSelected = currentIds.includes(numericId)
        return {
          ...state,
          deliveryZoneIds: isSelected
            ? currentIds.filter((id) => id !== numericId)
            : [ ...currentIds, numericId ]
        }
      })
    },
    [ setFormState ]
  )

  // Отображение полигонов на карте
  useEffect(() => {
    if (!map || allZones.length === 0) return

    // Удаляем старые полигоны
    polygonsOnMap.forEach((p) => p.removeFrom(map))

    const shopColorMap = new Map<GQL_ID, string>()
    const shopIds = [ ...new Set(allZones.map((z) => z.shop_id).filter(Boolean)) ]
    shopIds.forEach((sid, i) => {
      if (sid) shopColorMap.set(sid, getShopColor(i))
    })

    const newPolygons: L.Polygon[] = []
    const selectedIds = formState.deliveryZoneIds ?? []

    allZones.forEach((zone) => {
      if (!zone.zoneGeoDeliveryZone || zone.zoneGeoDeliveryZone.length === 0) return

      const isSelected = selectedIds.includes(Number(zone.id))
      const baseColor = zone.shop_id
        ? (shopColorMap.get(zone.shop_id) ?? '#e9833a')
        : '#e9833a'

      const polygon = L.polygon(
        zone.zoneGeoDeliveryZone.map(convertGeoPointToLatLng),
        {
          color: isSelected ? '#1675e0' : baseColor,
          fillColor: isSelected ? '#1675e0' : baseColor,
          fillOpacity: isSelected ? 0.45 : 0.2,
          weight: isSelected ? 3 : 1.5
        }
      ).addTo(map)

      polygon.bindTooltip(
        `${zone.name || `Зона #${zone.id}`}${zone.shop?.name ? ` — ${zone.shop.name}` : ''}`,
        { sticky: true }
      )

      polygon.on('click', () => toggleZoneSelection(zone.id))

      newPolygons.push(polygon)
    })

    setPolygonsOnMap(newPolygons)

    return () => {
      newPolygons.forEach((p) => p.removeFrom(map))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ map, allZones, formState.deliveryZoneIds, toggleZoneSelection ])

  // Центрирование карты по зонам
  useEffect(() => {
    if (map && allZones.length > 0 && !loadingZonesData) {
      const allPoints: LatLngLiteral[] = allZones.flatMap((zone) =>
        (zone.zoneGeoDeliveryZone ?? []).map(convertGeoPointToLatLng)
      )
      if (allPoints.length > 0) {
        const bounds = L.latLngBounds(allPoints)
        map.fitBounds(bounds, { padding: [ 30, 30 ] })
      }
    }
  }, [ map, allZones, loadingZonesData ])

  const handleFormSubmit = useCallback(
    (isOk = false) => {
      if (isOk) {
        saveCourier({
          variables: {
            courierInfo: mapStateToApiVariable(formState),
            upsert: !id
          }
        })
          .then(({ data }) => {
            notifyOk('Данные курьера сохранёны')
            setFormState((state) => mergeLeftOnly([ 'id' ], state, data?.courier))
            onSaved?.(data?.courier.id)
          })
          .catch(makeGraphQLErrorNotification('Ошибка сохранения данных'))
      }
      else {
        notifyError('Форма заполнена некорректно')
      }
    },
    [ formState, id, onSaved, saveCourier, setFormState ]
  )

  const removeZone = useCallback(
    (zoneId: number) => {
      setFormState((state) => ({
        ...state,
        deliveryZoneIds: (state.deliveryZoneIds ?? []).filter((id) => id !== zoneId)
      }))
    },
    [ setFormState ]
  )

  if (error) {
    return (
      <GraphQLCommonErrorDisplay
        error={error}
        title="Ошибка загрузки курьера"
      />
    )
  }

  return (
    <div className="CourierForm">
      <Form
        model={CourierFormValidationModel}
        formValue={formState}
        onChange={handleFormChange}
        onSubmit={handleFormSubmit}
        onCheck={handleFormError}
        ref={setFormRef}
        onError={handleFormError}
      >
        <Panel bordered className="CourierForm__section">
          <h4 className="CourierForm__sectionTitle">Личные данные</h4>
          <div className="CourierForm__row">
            <Form.Group>
              <Form.ControlLabel>Фамилия</Form.ControlLabel>
              <Form.Control name="firstName" />
            </Form.Group>
            <Form.Group>
              <Form.ControlLabel>Имя</Form.ControlLabel>
              <Form.Control name="lastName" />
            </Form.Group>
          </div>
          <div className="CourierForm__row">
            <Form.Group>
              <CustomerPhone name="phone" label="Телефон" />
            </Form.Group>
            <Form.Group>
              <Form.ControlLabel>Статус</Form.ControlLabel>
              <Form.Control
                name="status"
                accepter={SelectPicker}
                data={EMPLOYEE_STATUS_LABELS}
                searchable={false}
                cleanable={false}
              />
            </Form.Group>
          </div>
          <label className="CourierForm__checkbox">
            <FormCheckbox.Control name='is_shift_require_confirmation' />
            <span>Требовать подтверждение смены</span>
          </label>
        </Panel>

        <Panel bordered className="CourierForm__section">
          <h4 className="CourierForm__sectionTitle">Магазины</h4>
          <p className="CourierForm__hint">
            Курьер будет видеть <strong>все</strong> заказы выбранных магазинов, не взирая на настройки зон доставки.
          </p>
          <Form.Group>
            <Form.Control
              name="shopIds"
              sticky
              accepter={CheckPicker}
              loading={loadingShopsData}
              data={shops}
              checked={formState.shopIds}
              searchable={true}
              placeholder={'Выберите магазины'}
              style={{ width: 400 }}
            />
          </Form.Group>
        </Panel>

        <Panel bordered className="CourierForm__section">
          <h4 className="CourierForm__sectionTitle">Зоны доставки</h4>
          <p className="CourierForm__hint">
            Выберите зоны из списка или кликайте по зонам на карте.
          </p>

          <Form.Group>
            <Form.Control
              name="deliveryZoneIds"
              sticky
              accepter={CheckPicker}
              loading={loadingZonesData}
              data={zonesPickerData}
              checked={formState.deliveryZoneIds}
              searchable={true}
              placeholder={'Выберите зоны доставки'}
              style={{ width: 400 }}
            />
          </Form.Group>

          {selectedDeliveryZones.length > 0 && (
            <TagGroup className="CourierForm__tags">
              {selectedDeliveryZones.map(zone => (
                <Tag
                  key={zone.id}
                  size="md"
                  closable
                  onClose={() => removeZone(Number(zone.id))}
                  color="blue"
                >
                  {zone.name || `#${zone.id}`}
                  {zone.shop?.name ? ` — ${zone.shop.name}` : ''}
                </Tag>
              ))}
            </TagGroup>
          )}

          <div className="CourierForm__mapWrapper">
            {loadingZonesData && (
              <div className="CourierForm__mapLoader">
                <Loader size="md" content="Загрузка зон..." />
              </div>
            )}
            <MapContainer
              center={DEFAULT_MAP_CENTER}
              zoom={DEFAULT_MAP_ZOOM}
              scrollWheelZoom={true}
              whenCreated={setMap}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
            </MapContainer>
          </div>
        </Panel>

        <div className="CourierForm__actions">
          <SaveButton
            loading={loadingCourier || loadingSaveCourier}
            disabled={loadingCourier || loadingSaveCourier}
          >
            Сохранить
          </SaveButton>
        </div>
      </Form>
    </div>
  )
}
